import type { FC, ReactNode } from 'react';
import styles from './GlobalSmallCapLaunch.module.css';
import gscTeam from '../../images/gsc.jpg';
import jakeThomson from '../../images/jakeThomson.jpg';
import williamLough from '../../images/williamLough.jpg';
// ─── Types ──────────────────────────────────────────────────────────────────

interface PhotoPlaceholderProps {
    name: string;
    bg: string;
}

interface BioCardProps {
    name: string;
    photoSrc?: string;
    photoBg: string;
    children: ReactNode;
}

interface PhilosophyPillar {
    title: string;
    body: string;
}

// ─── Data ───────────────────────────────────────────────────────────────────

const PHILOSOPHY_PILLARS: PhilosophyPillar[] = [
    {
        title: 'Quality-Value',
        body: 'A finely-honed heuristic searching for resilient businesses with low leverage, combining quality screening with disciplined valuation.',
    },
    {
        title: 'Concentrated & Long-Only',
        body: 'A focused, long-only global small cap strategy designed to deliver attractive risk-adjusted returns from a target-rich, under-researched universe.',
    },
    {
        title: 'Active Ownership',
        body: 'Collaborative engagement with management teams to support shareholder value creation, applied consistently across the portfolio.',
    },
    {
        title: 'Long-Term Horizon',
        body: 'Patient capital with a research-intensive, long-term perspective — conviction built through deep fundamental analysis.',
    },
    {
        title: 'Valuation Disciplined',
        body: 'Rigorous price discipline ensures every position reflects an attractive margin of safety relative to intrinsic value.',
    },
    {
        title: 'Technology-Enhanced',
        body: 'A modern, research-intensive process harnessing recent technological advances to drive continuous improvement and exploit inefficiencies.',
    },
];

// ─── Sub-components ─────────────────────────────────────────────────────────

const PhotoPlaceholder: FC<PhotoPlaceholderProps> = ({ name, bg }) => (
    <div className={styles.twoColImage} style={{ background: bg }}>
        <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
            <circle cx="28" cy="22" r="12" stroke="white" strokeWidth="1.2" opacity="0.6" />
            <path
                d="M8 52c0-11.046 8.954-20 20-20s20 8.954 20 20"
                stroke="white"
                strokeWidth="1.2"
                strokeLinecap="round"
                opacity="0.6"
            />
        </svg>
        <p className={styles.twoColImageLabel}>{name}</p>
    </div>
);

const BioCard: FC<BioCardProps> = ({ name, photoSrc, photoBg, children }) => (
    <div className={styles.twoCol}>
        {photoSrc ? (
            <div className={styles.twoColImage}>
                <img src={photoSrc} alt={name} />
            </div>
        ) : (
            <PhotoPlaceholder name={name} bg={photoBg} />
        )}
        <div className={styles.twoColContent}>
            <h2>{name}</h2>
            {children}
        </div>
    </div>
);

// ─── Main component ──────────────────────────────────────────────────────────

const GlobalSmallCapLaunch: FC = () => {
    const contactEmail: string = 'e.pendlebury@otuscapital.com'
    return (
        <div className={styles.wrapper}>

            {/* ── HERO ── */}
            <section className={styles.hero}>
                <div className={styles.heroPhoto}>
                    {gscTeam ? (
                        <img src={gscTeam} alt="William Lough and Jake Thomson" />
                    ) : (
                        <>
                            <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
                                <rect x="1" y="1" width="50" height="50" rx="2" stroke="white" strokeWidth="1.2" />
                                <circle cx="19" cy="20" r="8" stroke="white" strokeWidth="1.2" />
                                <circle cx="33" cy="20" r="8" stroke="white" strokeWidth="1.2" />
                                <path
                                    d="M4 48c0-8.284 6.716-15 15-15s15 6.716 15 15"
                                    stroke="white"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M33 33c4.97 0 9 3.358 9 7.5"
                                    stroke="white"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                />
                            </svg>
                            <p className={styles.heroPhotoLabel}>Photo of William Lough &amp; Jake Thomson</p>
                        </>
                    )}
                </div>
                <div>
                    <div className={styles.heroKicker}>Press Release · 11th May 2026</div>
                    <h1 className={styles.heroTitle}>
                        William Lough &amp; Jake Thomson Join Otus Capital as Portfolio Managers
                    </h1>
                </div>
            </section>

            {/* ── INTRO ── */}
            <section className={styles.introStrip}>
                <p className={styles.introSub}>
                    We are delighted to announce that William Lough and Jake Thomson have joined Otus Capital
                    as Portfolio Managers and will be launching a strategy during H2 2026.
                </p>
                <br/>
                <p className={styles.introSub}>
                    William and Jake will be <strong>pioneering Otus' first global investment strategy specialising in
                    the small cap space</strong>, sitting alongside our existing strategies that focus on our core
                    competency as specialist small cap investors.
                </p>
            </section>
            {/* ── STRATEGY Description ── */}
            <section className={styles.strategyDescription}>
                <div className={styles.strategyDescriptionInner}>
                    <div className={styles.strategyDescriptionImages}>
                        <div className={styles.strategyDescriptionPortrait}>
                            <img src={jakeThomson} alt="Jake Thomson" />
                            <p className={styles.strategyDescriptionCaption}>Jake Thomson</p>
                        </div>
                        <div className={styles.strategyDescriptionPortrait}>
                            <img src={williamLough} alt="William Lough" />
                            <p className={styles.strategyDescriptionCaption}>William Lough</p>
                        </div>
                    </div>
                    <div className={styles.strategyDescriptionText}>
                <p className={styles.introSub}>
                    William and Jake each bring significant experience and expertise in the global small cap space with a combined 35 years of experience in equity markets. Most recently, Jake was a Head of Research at Ninety One, and William was a Partner and Portfolio Manager at River Global Investors.
                </p>
                <p className={styles.introSub}>
                    The team have developed a thoughtful quality-value philosophy, underpinned by their finely-honed heuristic, searching for resilient businesses with low leverage. They plan to launch a concentrated, long-only global small cap strategy and their philosophy is long-term in nature, valuation disciplined, and research-intensive. When applied to the inefficient small cap universe, this approach offers a compelling opportunity to deliver attractive risk-adjusted returns. Both investors have a track record of engaging collaboratively with management teams to support shareholder value creation and will apply a similarly active ownership approach to this strategy.
                </p>
                <p className={styles.introSub}>
                    We believe the combined team builds out Otus’ offering as a specialist small cap manager and look forward to learning from William and Jake’s global insights, harnessing strong investment collaboration and sharing small cap expertise between all our investment teams.
                </p>
                    </div>
                </div>
            </section>
            {/* ── LAUNCH DETAILS ── */}
            <div className={styles.launchStrip}>
                <div className={styles.launchIcon}>
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                </div>
                <div>
                    <h3>Strategy Launch — H2 2026</h3>
                    <p>
                        It is expected that the strategy will launch during H2 2026, with the seed investment having been provisionally agreed. Founders’ share classes will be available and further details are available upon request. 
                    </p>
                </div>
            </div>

            {/* ── EMMA QUOTE ── */}
            <section className={styles.quoteSectionDark}>
                <div className={styles.quoteInner}>
                    <blockquote>
                        "We are thrilled to be welcoming Will and Jake to the Otus team, and expanding on our expertise as European small cap specialists into the global universe. We remain completely focused on delivering best-in-class returns for all our investors and providing them with exposure to an asset class that is under-researched, exhibits inefficiencies, and offers ample alpha opportunities. We think the combination of our expertise and Will and Jake’s global insights and different investment approach will be beneficial for all our clients, and are excited to be evolving our line-up of strategies with their global strategy. As one of the longest-running small cap specialist firms globally, this is a natural extension for us and we look forward to collaborating with them during the years ahead."
                    </blockquote>
                    <cite className={styles.quoteCite}>
                        Emma Pendlebury — Head of Business Development, Otus Capital
                    </cite>
                </div>
            </section>

            {/* ── W&J QUOTE ── */}
            <section className={styles.quoteSection}>
                <div className={styles.quoteInner}>
                    <blockquote>
                        "Joining Otus Capital Management is a wonderful opportunity to build something special in the global small cap space. We share the firm’s conviction that in the investment world, ‘small is beautiful’. The small cap universe remains inefficient and under-researched – the ideal environment for focused stock selection to generate alpha – while smaller, capacity-constrained funds ensure our interests are perfectly aligned with our clients’ performance.
                        <p/>
                        In Otus, we have found partners who share our long-term horizon and singular focus on small cap investing excellence. Having the rare privilege of building a strategy from the ground up allows us to bring our fundamental investing DNA to life.  We have developed a robust process that harnesses the power of recent technological advances to deliver a modern, research-intensive approach to exploit our target-rich universe and drive continuous improvement in the years to come.”
                    </blockquote>
                    <cite className={styles.quoteCite}>
                        William Lough &amp; Jake Thomson — Portfolio Managers, Otus Capital
                    </cite>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className={styles.ctaSection}>
                <h2>Meet the Team</h2>
                <p>
                    To meet the team and find out more about their approach, please contact Emma: e.pendlebury@otuscapital.com
                </p>
                <a href={`mailto:${contactEmail}`} className={styles.btnOutline}>
                    Request More Details
                </a>
            </section>

            {/* ── DISCLAIMER ── */}
            <section className={styles.disclaimer}>
                <div className={styles.disclaimerInner}>
                    <p className={styles.disclaimerHeading}>IMPORTANT INFORMATION</p>
                    <p>This is a marketing communication. This is not a contractually binding document. This document refers to a proposed strategy and not one that is currently in place at Otus Capital Management. Do not base any final investment decision on this communication.</p>
                    <p>The content of this document has been approved and communicated by Otus Capital Management Limited ("OCM") for information purposes only. It does not constitute an offer or solicitation to any person in any jurisdiction to purchase or sell any investment. No information in this document should be construed as providing financial, investment, tax or other professional advice. The information contained herein is for the sole use of its intended recipient(s) and may not be copied, redistributed or published in whole or in part without the written consent of OCM. Information or opinions in this document may change without notice and OCM is under no obligation to bring any such changes to the attention of any recipient of this document.</p>
                    <p><strong>Limitation of Liability and Indemnity:</strong> The information herein is obtained from sources believed to be reliable but OCM provides no warranty of any kind, implied, expressed or statutory, in regards to the accuracy, adequacy or completeness of the information or data and expressly disclaims liability for errors or omissions in this information and data. OCM accepts no liability for any loss or damage arising out of the use or misuse of or reliance on the information provided including, without limitation, any loss of profits or any other damage, direct or consequential. You agree to indemnify and hold harmless OCM, its affiliates, directors and employees from and against any and all liabilities, claims, damages, losses or expenses, including legal fees and expenses, arising out of your access to or use of the information in this document, save to the extent that such losses or expenses may not be excluded pursuant to relevant law or regulation.</p>
                    <p><strong>Copyright and Other Rights:</strong> The copyright, trademarks and all similar rights of this document and the contents, including all information, graphics, code, text and design, are owned by OCM.</p>
                    <p>Otus Capital Management is authorised and regulated by the Financial Conduct Authority "FCA" (FCA Firm Reference Number 195277). The firm's registered address is 29 Queen Anne's Gate, London SW1H 9BU.</p>
                </div>
            </section>

        </div>
    );
};

export default GlobalSmallCapLaunch;
